/* Callouts / GFM alerts (ADR-0722) para o renderer de Markdown. A sintaxe do
   GitHub (`> [!NOTE]` … `> [!CAUTION]`) vira admonition estilizado — ícone lucide
   + cor por tipo via tokens shadcn (sem cor crua). Duas peças:

   1. `remarkCallouts` — plugin remark leve (sem dep externa): reescreve o
      blockquote cujo primeiro parágrafo começa com `[!TIPO]` num
      `<div class="md-callout md-callout-TIPO">` (via `data.hName`/`hProperties`)
      e remove o marcador. O rehype-sanitize (markdown.tsx) só permite esses
      classNames — nenhum className arbitrário passa.
   2. `MarkdownCallout` — o override de `<div>` no react-markdown detecta a classe
      `md-callout-*` e renderiza este componente, resolvendo ícone/cor/rótulo. */
import {
  InfoIcon,
  LightbulbIcon,
  type LucideIcon,
  MessageSquareWarningIcon,
  OctagonAlertIcon,
  TriangleAlertIcon,
} from 'lucide-react';
import type { JSX, ReactNode } from 'react';
import { type TranslationKey, uiText } from '../i18n.js';
import { cn } from '../lib/utils.js';

type CalloutType = 'note' | 'tip' | 'important' | 'warning' | 'caution';

const CALLOUT_TYPES: readonly CalloutType[] = ['note', 'tip', 'important', 'warning', 'caution'];

/** classNames que o remark plugin emite — o rehype-sanitize precisa permitir só estes. */
export const CALLOUT_CLASSNAMES: readonly string[] = [
  'md-callout',
  ...CALLOUT_TYPES.map((type) => `md-callout-${type}`),
];

interface CalloutStyle {
  readonly icon: LucideIcon;
  readonly labelKey: TranslationKey;
  /* Classes estáticas (Tailwind não resolve interpolação) — borda/realce + cor do
     título/ícone via token semântico shadcn. */
  readonly container: string;
  readonly title: string;
}

const CALLOUT_STYLES: Record<CalloutType, CalloutStyle> = {
  note: {
    icon: InfoIcon,
    labelKey: 'markdown.callout.note',
    container: 'border-info/30 bg-info/5',
    title: 'text-info',
  },
  tip: {
    icon: LightbulbIcon,
    labelKey: 'markdown.callout.tip',
    container: 'border-success/30 bg-success/5',
    title: 'text-success',
  },
  important: {
    icon: MessageSquareWarningIcon,
    labelKey: 'markdown.callout.important',
    container: 'border-accent/30 bg-accent/5',
    title: 'text-accent',
  },
  warning: {
    icon: TriangleAlertIcon,
    labelKey: 'markdown.callout.warning',
    container: 'border-warning/30 bg-warning/5',
    title: 'text-warning',
  },
  caution: {
    icon: OctagonAlertIcon,
    labelKey: 'markdown.callout.caution',
    container: 'border-destructive/30 bg-destructive/5',
    title: 'text-destructive',
  },
};

/** Deriva o tipo do callout a partir do className do `<div>` (string ou lista). */
export function calloutTypeFromClassName(className: unknown): CalloutType | undefined {
  const classes = Array.isArray(className)
    ? className.map((entry) => String(entry))
    : typeof className === 'string'
      ? className.split(/\s+/)
      : [];
  if (!classes.includes('md-callout')) return undefined;
  return CALLOUT_TYPES.find((type) => classes.includes(`md-callout-${type}`));
}

/** Admonition estilizado renderizado pelo override de `<div>` do markdown. */
export function MarkdownCallout(props: {
  readonly type: CalloutType;
  readonly children: ReactNode;
}): JSX.Element {
  const { type, children } = props;
  const style = CALLOUT_STYLES[type];
  const Icon = style.icon;
  return (
    <div
      data-slot="callout"
      data-callout-type={type}
      className={cn('my-3 rounded-md border px-4 py-3 text-sm', style.container)}
    >
      <div className={cn('mb-1 flex items-center gap-2 font-semibold', style.title)}>
        <Icon className="size-4 shrink-0" aria-hidden="true" />
        {uiText(style.labelKey)}
      </div>
      <div className="text-foreground [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">{children}</div>
    </div>
  );
}

/* --- remark plugin (mdast) --------------------------------------------------- */

/* Tipagem estrutural mínima do nó mdast (os pacotes `mdast`/`unist-util-visit`
   não são hoisted no monorepo; evitamos import não-resolvível). */
interface MdNode {
  type: string;
  value?: string;
  children?: MdNode[];
  data?: { hName?: string; hProperties?: Record<string, unknown> };
}

const CALLOUT_MARKER = /^\[!(NOTE|TIP|IMPORTANT|WARNING|CAUTION)\][^\S\n]*/;

function isMdNode(value: unknown): value is MdNode {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { type?: unknown }).type === 'string'
  );
}

function applyCallout(node: MdNode): void {
  const firstParagraph = node.children?.[0];
  if (firstParagraph === undefined || firstParagraph.type !== 'paragraph') return;
  const marker = firstParagraph.children?.[0];
  if (marker === undefined || marker.type !== 'text' || typeof marker.value !== 'string') return;
  const match = CALLOUT_MARKER.exec(marker.value);
  if (match === null) return;

  const type = (match[1] ?? '').toLowerCase();
  const rest = marker.value.slice(match[0].length);
  if (rest.length > 0) {
    marker.value = rest;
  } else {
    /* Marcador sozinho na linha → remove o nó de texto e a quebra que o segue,
       para o conteúdo não começar com um <br>. */
    firstParagraph.children?.shift();
    if (firstParagraph.children?.[0]?.type === 'break') firstParagraph.children.shift();
  }

  node.data = {
    hName: 'div',
    hProperties: { className: ['md-callout', `md-callout-${type}`] },
  };
}

function visitNode(node: unknown): void {
  if (!isMdNode(node)) return;
  if (node.type === 'blockquote') applyCallout(node);
  if (node.children !== undefined) {
    for (const child of node.children) visitNode(child);
  }
}

/** Plugin remark que converte blockquotes `[!TIPO]` em callouts. */
export function remarkCallouts(): (tree: unknown) => void {
  return (tree: unknown): void => {
    visitNode(tree);
  };
}
